// AboutUs.js
import React from "react";
import { useNavigate } from "react-router-dom";

export default function AboutUs() {
  const navigate = useNavigate();
  const handleGoToQuiz = () => navigate("/", { state: { scrollToQuiz: true } });

  return (
    <section className="about-root" aria-label="About Vybly">
      <style>{`
        :root{
          --navy:#0b1a22;
          --navy-2:#08151b;
          --teal:#66afad;
          --gold:#ffbf00;

          /* glass + type */
          --glass:#0a1d24cc;           /* translucent panel */
          --glass-border:#ffffff1a;
          --glass-ring:#66afad55;
          --ink:#f6fbff;               /* high-contrast body text */
          --muted:#cfe1e4;
        }

        *{box-sizing:border-box}

        /* deep backdrop; content scrolls */
        .about-root{
          position:fixed; inset:0; width:100vw; height:100vh; overflow:hidden;
          background:
            radial-gradient(1100px 620px at 18% -6%, #12303a 0%, transparent 60%),
            radial-gradient(900px 520px at 82% 18%, #0e2129 0%, transparent 55%),
            linear-gradient(180deg, var(--navy), var(--navy-2));
          color:var(--ink);
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
        }
        .scroll{ height:100%; overflow-y:auto; padding:clamp(28px,5vw,60px); }
        .wrap{ width:min(1000px,100%); margin-inline:auto; position:relative; z-index:1; }

        /* more top spacing for title */
        .hero{ text-align:center; padding-top: clamp(36px, 8vh, 96px); margin-bottom: clamp(18px,3.6vw,28px); }
        .title{
          margin:0 0 10px; font-weight:900; letter-spacing:-.02em; line-height:1.03;
          font-size:clamp(40px,6.5vw,72px); color:var(--gold);
          text-shadow:0 2px 0 #0004, 0 14px 32px #000a;
        }
        .subtitle{
          display:block; font-weight:800; color:var(--teal);
          font-size:clamp(16px,2.1vw,20px); opacity:.95;
        }

        /* glassy dark card */
        .card{
          background:var(--glass);
          border:1px solid var(--glass-border);
          border-radius:22px;
          padding:clamp(24px,3vw,32px);
          box-shadow: 0 16px 44px #000a, inset 0 1px 0 #ffffff14;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          outline: 1px solid var(--glass-ring);
          color:var(--ink);
        }

        .prose{
          font-size:clamp(18px,2.05vw,20px);
          line-height:1.78;
          letter-spacing:.01em;
        }
        .prose p{ margin:0 0 18px; }
        .prose ul{ margin: 6px 0 20px; }

        /* emphasized sections (readable on dark glass) */
        .callout{
          background: linear-gradient(180deg, rgba(255,191,0,.12), rgba(255,191,0,.10));
          border:1px solid rgba(255,191,0,.55);
          border-radius:16px;
          padding:16px 18px;
          margin:20px 0 16px;
          box-shadow: inset 0 1px 0 #ffffff1c;
        }
        .callout strong{ color:#ffd466; font-weight:800; }

        .beliefs{
          background: linear-gradient(180deg, rgba(102,175,173,.14), rgba(102,175,173,.12));
          border:1px solid rgba(102,175,173,.55);
          border-radius:16px;
          padding:18px 18px;
          margin:20px 0 18px;
          box-shadow: inset 0 1px 0 #ffffff1c;
        }

        .cta{ text-align:center; margin:26px 0 6px; }
        .btn{
          appearance:none; border:none; cursor:pointer; padding:14px 22px; border-radius:14px;
          font-weight:800; letter-spacing:.3px;
          background:var(--gold); color:#111;
          box-shadow:0 12px 26px rgba(255,191,0,.35), 0 0 0 4px rgba(255,191,0,.12);
          transition:transform .12s ease, filter .12s ease;
        }
        .btn:hover{ transform:translateY(-2px); filter:brightness(1.02); }

        .sig{ text-align:center; color:var(--muted); font-style:italic; margin-top:12px; }

        /* subtle brand hexes */
        .hex{
          position:absolute; width:clamp(60px,9vw,110px); aspect-ratio:1/1;
          background:var(--teal); opacity:.9;
          clip-path: polygon(25% 6.7%,75% 6.7%,100% 50%,75% 93.3%,25% 93.3%,0% 50%);
          filter: drop-shadow(0 12px 24px #0006);
          animation: float 12s ease-in-out infinite; pointer-events:none;
        }
        .hex.alt{ background: var(--gold); opacity:.95; }
        .hex.a{ top:8%; left:12%; animation-delay:-2s; }
        .hex.b{ top:28%; right:18%; transform:scale(.95); animation-delay:-5s; }
        .hex.c{ bottom:16%; right:8%; transform:scale(1.05); }
        .hex.d{ bottom:10%; left:6%; transform:scale(.8); animation-delay:-3s; }
        @keyframes float{ 0%,100%{ transform:translateY(0) } 50%{ transform:translateY(-14px) } }
        @media (prefers-reduced-motion: reduce){ .hex{ animation:none } }

        .wave{ position:absolute; left:0; right:0; bottom:-1px; height:140px; width:100%; z-index:0; }
      `}</style>

      {/* brand hexes */}
      <span className="hex a" />
      <span className="hex alt b" />
      <span className="hex c" />
      <span className="hex alt d" />

      <div className="scroll">
        <div className="wrap">
          <header className="hero">
            <h1 className="title">2AM on the I-5</h1>
            <span className="subtitle">Where Vybly was born</span>
          </header>

          {/* ORIGINAL TEXT ONLY */}
          <article className="card prose" aria-label="Origin story">
            <p>
              I didn’t build Vybly because I’m a productivity nut or because I needed another excuse to avoid my to-do list.<br /><br />
              Vybly was born somewhere on the 5 between Palo Alto and LA, usually at 2am, driving through dead cell zones with nothing but my own thoughts for company. If you’re a high-performer (or just a relentless doer) maybe you know the feeling: your brain’s running 47 mental tabs, and every one is screaming for attention.
            </p>
            <p>
              Back in 2017, I was at Stanford, trying to compress a two-year master’s into one (because I had a life and a relationship to get back to in LA). Add to that being in the middle of thesis research at NASA-JPL on hybrid rocket engines, driving to LA every other week, and becoming a SCUBA Divemaster so I could get paid instead of paying for my own hobby. Later, I added “commercial helicopter pilot” to the mix (because apparently, my brain loves chaos).
            </p>
            <p>
              What I learned, again and again, was that the biggest obstacles weren’t technical.<br />
              Sure, sometimes it was hard science or a gnarly deadline.<br />
              But 90% of my challenge (and the challenge of every ambitious person I’ve ever admired) was simply keeping my mind clear enough to perform, make decisions, and not freeze up under pressure.
            </p>

            {/* Emphasis 1 — Decision fatigue */}
            <div className="callout">
              <p>
                <strong>Decision fatigue is real.</strong> It eats time, energy, and focus.<br />
                And all those “mental tabs” you’re holding open – regrets, next steps, unfinished ideas, unresolved feelings – they all tax your brain’s bandwidth.
              </p>
            </div>

            <p>
              During those long solo drives, I started my own version of what I’d now call “tabbing out”: just talking to myself, sorting the chaos, closing open loops, forcing clarity.<br />
              It was a game-changer.
            </p>
            <p>But here’s the truth:</p>
            <ul style={{marginBottom: 20, marginTop: 4, paddingLeft: 25, fontSize: "1em"}}>
              <li>Most of us don’t have hours to think, or the lucky gift of an insightful friend who really gets how our brains work.</li>
              <li>And let’s be honest: sometimes we’re our own worst enemy, hiding from what we don’t want to see.</li>
            </ul>
            <p>
              That’s why Vybly exists.<br />
              Not as a therapist. Not as an assistant.<br />
              But as the first platform built for people who need to get shit done; who need to “tab out,” organize their minds, and tune back in to what matters.
            </p>

            {/* Emphasis 2 — Beliefs */}
            <div className="beliefs" role="region" aria-label="Our belief">
              <p><strong>At Vybly, we believe:</strong></p>
              <p>Most productivity tools miss the point. It’s not just about tracking tasks, it’s about clearing the mental clutter so you can act.</p>
              <p>Wellness apps tell you to “just breathe” and recite affirmations. That’s not enough when your brain feels like a browser with 20 tabs open.</p>
              <p>Therapy is powerful, but most days, you just need a way to see your own mental blind spots and get moving.</p>
            </div>

            <p>
              So I built Vybly as the tool I wish I’d had on those midnight drives - a private, smart, judgment-free space where you can dump your thoughts, sort your chaos, and see actionable paths forward.<br />
              It all starts with an evaluation, because knowing how your mind works is the first step to making it work for you.
            </p>
            <p>
              Whether you’re a nurse, a founder, a pilot, or anyone trying to perform at your best:<br />
              If your head ever feels too full, and you’re sick of “just meditating” or “being positive,” maybe it’s time to tab out. Tune in.
            </p>

            <div className="cta">
              <button className="btn" onClick={handleGoToQuiz}>Let’s get you back in the flow.</button>
            </div>

            <div className="sig">— Michael Preudhomme,<br />Founder, Vybly</div>
          </article>
        </div>
      </div>

      {/* brand wave footer (teal) */}
      <svg className="wave" viewBox="0 0 1440 140" preserveAspectRatio="none" aria-hidden>
        <path fill="#66afad" d="M0,110 C220,170 360,30 560,66 C800,106 980,190 1440,60 L1440,140 L0,140 Z"/>
      </svg>
    </section>
  );
}
